import { Injectable } from '@angular/core';

/** How long to wait for the Maps script before giving up and letting the app boot anyway. */
const LOAD_TIMEOUT_MS = 15000;

/**
 * Resolves when the Google Maps JS API (loaded async from index.html) is available.
 *
 * The script tag registers `window.__onGoogleMapsLoaded` as its `callback` and exposes
 * `window.__googleMapsLoaded` (a promise) before it; this service awaits that promise.
 * An app initializer (main.ts) blocks bootstrap on `whenReady()` so components can use
 * the `google` global directly, as they already do throughout the app. The promise
 * never rejects: it resolves `false` on timeout or script failure, so a blocked script
 * degrades to the maps not rendering instead of the whole app never booting.
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  private readonly ready = this.buildReadyPromise();

  /** Resolves true once `google.maps` exists, false if the script failed or timed out. */
  whenReady(): Promise<boolean> {
    return this.ready;
  }

  get isLoaded(): boolean {
    return !!(window as any).google?.maps;
  }

  private buildReadyPromise(): Promise<boolean> {
    if (this.isLoaded) {
      return Promise.resolve(true);
    }

    const timeout = new Promise<boolean>((resolve) => {
      setTimeout(() => {
        if (!this.isLoaded) {
          console.error(`Google Maps API not available after ${LOAD_TIMEOUT_MS}ms — maps will not render.`);
        }
        resolve(this.isLoaded);
      }, LOAD_TIMEOUT_MS);
    });

    const scriptLoaded = (window as any).__googleMapsLoaded as Promise<boolean> | undefined;
    if (scriptLoaded) {
      return Promise.race([scriptLoaded, timeout]);
    }

    // No index.html hook (e.g. tests): poll for the global instead.
    const polled = new Promise<boolean>((resolve) => {
      const timer = setInterval(() => {
        if (this.isLoaded) {
          clearInterval(timer);
          resolve(true);
        }
      }, 100);
      timeout.then(() => {
        clearInterval(timer);
        resolve(this.isLoaded);
      });
    });
    return polled;
  }
}
